// Pestaña «Ventas» de la ficha de Tiendas: filtro por categoría y la tabla de tickets del periodo.
import { useMemo, useState, type ReactNode } from 'react';
import { LayoutGrid } from 'lucide-react';
import { Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import type { ProductCategory } from '@/domains/sales/wizard/types';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { DetailCard } from '@/shared/patterns/DetailCard';
import { NoSourceBlock } from '@/shared/patterns/NoSource';
import {
    CategoryIcon,
    saleProductName,
    SaleTicketProduct,
    ticketHeadline,
    ticketTitle,
    useTicketExpansion,
} from '@/shared/patterns/SaleTicket';
import { buildCategoryMix, saleCategoryKeys, type CategoryMixKey } from '../lib/categoryMix';
import { money, paymentLabel } from '../lib/tiendasFormat';

/** Por qué no se pueden leer las ventas de la tienda. `ok` es que sí. */
export type SalesBlock = 'ok' | 'noRetailer' | 'noPermission';

const SALES_BLOCKED: Record<Exclude<SalesBlock, 'ok'>, string> = {
    noRetailer:
        'El listado de ventas se consulta por cadena y tu rol abarca más de una. Ábrelo en la pantalla de Ventas.',
    noPermission: 'Tu rol no incluye el listado de ventas, que es de donde sale este bloque.',
};

export interface StoreSalesState {
    block: SalesBlock;
    items: BackofficeSale[] | undefined;
    truncated: boolean;
    isLoading: boolean;
    error: Error | null;
    legacyCategories: ReadonlyMap<string, ProductCategory>;
}

export function CategoryCard({
    icon,
    label,
    salesCount,
    amountCents,
    unit = 'sale',
    active = false,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    salesCount: number;
    amountCents: number;
    unit?: 'sale' | 'piece';
    active?: boolean;
    onClick?: () => void;
}) {
    const body = (
        <>
            <span className="mt-0.5 flex-none text-muted-foreground">{icon}</span>
            <span className="min-w-0 text-left">
                <span className="block truncate text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                    {label}
                </span>
                <span className="block text-[14px] font-bold tabular-nums">
                    {salesCount}{' '}
                    <span className="text-[10px] font-medium text-muted-foreground">
                        {unit === 'piece'
                            ? salesCount === 1
                                ? 'pieza'
                                : 'piezas'
                            : salesCount === 1
                              ? 'venta'
                              : 'ventas'}
                    </span>
                </span>
                <span className="block text-[10.5px] tabular-nums text-muted-foreground">
                    {money(amountCents)}
                </span>
            </span>
        </>
    );
    const className = cn(
        'flex min-w-0 items-start gap-2 rounded-md border px-2.5 py-2',
        active ? 'border-primary/50 bg-muted' : 'border-border bg-muted/20',
        salesCount === 0 && 'opacity-60',
    );
    return onClick ? (
        <button
            type="button"
            aria-pressed={active}
            onClick={onClick}
            className={cn(className, 'hover:bg-accent')}
        >
            {body}
        </button>
    ) : (
        <div className={className}>{body}</div>
    );
}

export function SalesUnavailable({ sales }: { sales: StoreSalesState }) {
    if (sales.block !== 'ok') return <NoSourceBlock>{SALES_BLOCKED[sales.block]}</NoSourceBlock>;
    if (sales.isLoading) return <Skeleton lines={3} />;
    return (
        <NoSourceBlock>
            No se pudieron leer las ventas del periodo
            {sales.error ? `: ${sales.error.message}` : '.'}
        </NoSourceBlock>
    );
}

type SaleSortKey = 'date' | 'customer' | 'seller' | 'product' | 'payment' | 'amount';

function saleSortValue(
    sale: BackofficeSale,
    key: SaleSortKey,
    category: CategoryMixKey | null,
): string | number {
    switch (key) {
        case 'date':
            return sale.soldAt;
        case 'customer':
            return saleCustomerLabel(sale) ?? '';
        case 'seller':
            return sale.sellerName ?? '';
        case 'product':
            return ticketHeadline(sale, category, saleProductName(sale)).name;
        case 'payment':
            return paymentLabel(sale) ?? '';
        case 'amount':
            return soldCentsOf(sale);
    }
}

export function StoreSalesTab({ sales }: { sales: StoreSalesState }) {
    const [category, setCategory] = useState<CategoryMixKey | null>(null);
    const [sort, setSort] = useState<{ key: SaleSortKey; direction: 'asc' | 'desc' }>({
        key: 'date',
        direction: 'desc',
    });
    const active = useMemo(
        () => sales.items?.filter((sale) => sale.cancelledAt === null),
        [sales.items],
    );
    const mix = useMemo(
        () => (active ? buildCategoryMix(active, sales.legacyCategories) : []),
        [active, sales.legacyCategories],
    );
    const { expandColumn, tableProps } = useTicketExpansion<BackofficeSale, SaleSortKey>({
        category,
        totalCentsOf: soldCentsOf,
    });

    if (!active) {
        return (
            <div className="p-3">
                <SalesUnavailable sales={sales} />
            </div>
        );
    }

    const filtered =
        category === null
            ? active
            : active.filter((sale) =>
                  saleCategoryKeys(sale, sales.legacyCategories).includes(category),
              );
    const sign = sort.direction === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
        const av = saleSortValue(a, sort.key, category);
        const bv = saleSortValue(b, sort.key, category);
        const cmp =
            typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), 'es-MX');
        return cmp * sign;
    });
    const latest = active.reduce<string | null>(
        (max, sale) => (max === null || sale.soldAt > max ? sale.soldAt : max),
        null,
    );
    const totalCents = active.reduce((sum, sale) => sum + soldCentsOf(sale), 0);

    const columns: DataTableColumn<BackofficeSale, SaleSortKey>[] = [
        expandColumn,
        {
            key: 'date',
            label: 'Fecha',
            width: '90px',
            sortKey: 'date',
            cellClassName: () => 'text-muted-foreground',
            cell: (sale) => formatDateMx(sale.soldAt),
        },
        {
            key: 'customer',
            label: 'Cliente',
            width: '22%',
            sortKey: 'customer',
            cellTitle: (sale) => saleCustomerLabel(sale) ?? undefined,
            cell: (sale) => saleCustomerLabel(sale) ?? 'Sin nombre',
        },
        {
            key: 'seller',
            label: 'Vendedor',
            width: '20%',
            sortKey: 'seller',
            cellTitle: (sale) => sale.sellerName ?? undefined,
            cell: (sale) => sale.sellerName ?? '—',
        },
        {
            key: 'product',
            label: 'Producto',
            sortKey: 'product',
            cellTitle: (sale) => ticketTitle(sale, saleProductName(sale)),
            cell: (sale) => (
                <SaleTicketProduct
                    sale={sale}
                    category={category}
                    saleName={saleProductName(sale)}
                    saleIcon={
                        <CategoryIcon
                            category={saleCategoryKeys(sale, sales.legacyCategories)[0]}
                        />
                    }
                />
            ),
        },
        {
            key: 'payment',
            label: 'Forma de pago',
            width: '88px',
            sortKey: 'payment',
            cellOverflow: 'nowrap',
            cell: (sale) => {
                const label = paymentLabel(sale);
                if (label === null) return <span className="text-muted-foreground">—</span>;
                return (
                    <span
                        className={cn(
                            'rounded px-1.5 py-px text-[9.5px] font-semibold',
                            label === 'Contado' && 'bg-muted text-muted-foreground',
                            label === 'Crédito SK' && 'bg-primary/10 text-primary',
                            label !== 'Contado' && label !== 'Crédito SK' && 'text-info',
                        )}
                    >
                        {label}
                    </span>
                );
            },
        },
        {
            key: 'amount',
            label: 'Monto',
            width: '86px',
            numeric: true,
            sortKey: 'amount',
            cellClassName: () => 'font-bold',
            cell: (sale) => money(soldCentsOf(sale)),
        },
    ];

    return (
        <div className="p-3">
            <DetailCard title="Filtrar por categoría">
                <div className="grid grid-cols-3 gap-2">
                    <CategoryCard
                        icon={<LayoutGrid className="size-4" aria-hidden />}
                        label="Todos"
                        salesCount={active.length}
                        amountCents={totalCents}
                        active={category === null}
                        onClick={() => {
                            setCategory(null);
                        }}
                    />
                    {mix.map((slice) => (
                        <CategoryCard
                            key={slice.category}
                            icon={<CategoryIcon category={slice.category} />}
                            label={slice.label}
                            salesCount={slice.salesCount}
                            amountCents={slice.amountCents}
                            active={category === slice.category}
                            onClick={() => {
                                setCategory(slice.category);
                            }}
                        />
                    ))}
                </div>
            </DetailCard>
            <DetailCard
                title={`Ventas · ${String(filtered.length)} ${filtered.length === 1 ? 'ticket' : 'tickets'}`}
                meta={
                    category !== null ? (
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => {
                                setCategory(null);
                            }}
                        >
                            × quitar filtro
                        </button>
                    ) : latest ? (
                        `última: ${formatDateMx(latest)}`
                    ) : undefined
                }
                flush
                footnote={
                    sales.truncated
                        ? 'Hay más ventas en el periodo de las que se leyeron; ábrelas en la pantalla de Ventas.'
                        : undefined
                }
            >
                <div className="flex max-h-[420px] min-h-0 flex-col">
                    <DataTable
                        columns={columns}
                        rows={sorted}
                        rowKey={(sale) => sale.saleId}
                        sort={sort}
                        onSortToggle={(key) => {
                            setSort((current) =>
                                current.key === key
                                    ? {
                                          key,
                                          direction: current.direction === 'asc' ? 'desc' : 'asc',
                                      }
                                    : {
                                          key,
                                          direction:
                                              key === 'date' || key === 'amount' ? 'desc' : 'asc',
                                      },
                            );
                        }}
                        {...tableProps}
                        caption="Ventas de la tienda en el periodo."
                        empty={
                            <span className="italic">
                                Sin ventas registradas
                                {category !== null ? ' en la categoría seleccionada' : ''}
                            </span>
                        }
                    />
                </div>
            </DetailCard>
        </div>
    );
}
